import { useCallback } from 'react';
import ErrorHelpers from '~/app/errorHelpers.js';
import { SPIN_SAVE_MSG } from './useSpinner';

/**
 * useSubmitForReview
 *
 * Owns the three submit-for-review methods that were previously class methods
 * on FormViewer:
 *
 *   reloadBilling()              — triggers a billing reload via calcAPI
 *   setReturnReviewStatusDialog()— opens the submit-for-review confirm dialog
 *   handleConfirmSubmitForReview()— confirms submit/retract, saves return
 *
 * Returns:
 *   { reloadBilling, setReturnReviewStatusDialog, handleConfirmSubmitForReview }
 */
const useSubmitForReview = ({
  calcAPIRef,
  // Kept-current refs to hook results
  queueReturnRef, // kept-current ref to useQueueReturn result
  returnCloseRef, // kept-current ref to useReturnClose result
  spinnerRef, // kept-current ref to useSpinner result
  // Props / Redux actions
  isReadOnlyRef, // kept-current ref to props.isReadOnly
  toggleSubmitForReviewDlg, // Redux action
}) => {
  /**
   * Triggers a billing reload on the calc server.
   */
  const reloadBilling = useCallback(() => {
    calcAPIRef.current.ReturnReloadBilling();
  }, [calcAPIRef]);

  /**
   * Opens the submit-for-review confirmation dialog.
   */
  const setReturnReviewStatusDialog = useCallback(() => {
    toggleSubmitForReviewDlg(true);
  }, [toggleSubmitForReviewDlg]);

  /**
   * Confirms submit/retract for review: updates queue status, closes dialog,
   * then saves the return.
   */
  const handleConfirmSubmitForReview = useCallback(async () => {
    queueReturnRef.current.submitReturnReviewStatus();
    toggleSubmitForReviewDlg(false);
    try {
      spinnerRef.current.startSpin(SPIN_SAVE_MSG);
      await calcAPIRef.current.ReturnSave(
        returnCloseRef.current.getReturnMetadata(),
        isReadOnlyRef.current,
      );
    } catch (error) {
      ErrorHelpers.handleError('Error saving return', error);
    }
  }, [
    calcAPIRef,
    isReadOnlyRef,
    queueReturnRef,
    returnCloseRef,
    spinnerRef,
    toggleSubmitForReviewDlg,
  ]);

  return {
    reloadBilling,
    setReturnReviewStatusDialog,
    handleConfirmSubmitForReview,
  };
};

export default useSubmitForReview;
